import flowValveApi from '@/api/config/flowValveApi'
import { queryKeys } from '@/constant/common'
import { useQuery } from '@tanstack/react-query'

/**
 * Key for every flow valve and pump on one interface. It starts with
 * `queryKeys.flowValve`, so anything that invalidates the valve lists
 * refreshes this one too.
 */
export const flowValvesForPurposeCheckKey = (interfaceId: number) => [
  queryKeys.flowValve,
  'purposeCheck',
  Number(interfaceId),
]

/**
 * SCRUM-1032 — every flow valve and pump on the controller, for the decoder
 * purpose check.
 *
 * Not `useSearchFlowValve`. That one sends no `PageSize`, so the backend
 * returns its default page of 10, and its key has no interface id, so another
 * controller's list could answer for this one. A purpose check that reads only
 * ten valves misses the rest and lets a purpose change orphan them.
 * `PageSize: 0` is "all of them" on this endpoint.
 */
export const fetchFlowValvesForPurposeCheck = async (interfaceId: number) => {
  const { data } = await flowValveApi.getAll({
    interfaceId,
    PageSize: 0,
  })
  return Array.isArray(data?.data) ? data.data : []
}

const useFlowValvesForPurposeCheck = (
  interfaceId: number,
  { enabled = true }: { enabled?: boolean } = {},
) =>
  useQuery(
    flowValvesForPurposeCheckKey(interfaceId),
    () => fetchFlowValvesForPurposeCheck(interfaceId),
    { enabled: enabled && Number(interfaceId) > 0 },
  )

export default useFlowValvesForPurposeCheck
